import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UltravoxToolData } from '../../../schemas/apis/interface';

// TypeScript interfaces for tool creation
interface ToolHeader {
  key: string;
  value: string;
  required: boolean;
}

interface ToolParameter {
  name: string;
  description: string;
  type: 'string' | 'number' | 'integer' | 'boolean' | 'custom';
  location: 'query' | 'header' | 'path' | 'body';
  parameterType: 'dynamic' | 'static' | 'automatic';
  required: boolean;
  knownValue: string;
  value?: string;
  customSchema?: any; // Change from object to any
}

interface ToolData {
  name: string;
  description: string;
  toolType: 'HTTP' | 'Client';
  customEndpointUrl: string;
  httpMethod: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  timeoutSeconds: number;
  headers: ToolHeader[];
  parameters: ToolParameter[];
  action: 'default' | 'speaks' | 'listens' | 'speaks_once';
  staticResponse: string;
  precomputable: boolean;
  staticResponseEnabled: boolean;
}

interface ToolsState {
  tools: UltravoxToolData[];
  isLoading: boolean;
  error: string | null;
  editingTool: UltravoxToolData | null;
  isToolCreatorOpen: boolean;

  // Tool creation form state
  toolData: ToolData;
  errors: Record<string, string>;
  hasAttemptedSave: boolean;

  // Header management state
  isAddingHeader: boolean;
  isEditingHeader: boolean;
  editingHeaderIndex: number;
  newHeader: ToolHeader;

  // Parameter management state
  isAddingParameter: boolean;
  isEditingParameter: boolean;
  editingParameterIndex: number;
  newParameter: ToolParameter;
}

const initialToolData: ToolData = {
  name: '',
  description: '',
  toolType: 'HTTP',
  customEndpointUrl: '',
  httpMethod: 'GET',
  timeoutSeconds: 20,
  headers: [],
  parameters: [],
  action: 'default',
  staticResponse: '',
  precomputable: false,
  staticResponseEnabled: false,
};

const initialHeader: ToolHeader = {
  key: '',
  value: '',
  required: false,
};

const initialParameter: ToolParameter = {
  name: '',
  description: '',
  type: 'string',
  location: 'query',
  parameterType: 'dynamic',
  required: false,
  knownValue: '',
};

const initialState: ToolsState = {
  tools: [],
  isLoading: false,
  error: null,
  editingTool: null,
  isToolCreatorOpen: false,

  // Tool creation form state
  toolData: initialToolData,
  errors: {},
  hasAttemptedSave: false,

  // Header management state
  isAddingHeader: false,
  isEditingHeader: false,
  editingHeaderIndex: -1,
  newHeader: initialHeader,

  // Parameter management state
  isAddingParameter: false,
  isEditingParameter: false,
  editingParameterIndex: -1,
  newParameter: initialParameter,
};

const toolsSlice = createSlice({
  name: 'tools',
  initialState,
  reducers: {
    setTools: (state, action: PayloadAction<UltravoxToolData[]>) => {
      state.tools = action.payload;
    },
    addTool: (state, action: PayloadAction<UltravoxToolData>) => {
      state.tools.push(action.payload);
    },
    updateTool: (state, action: PayloadAction<UltravoxToolData>) => {
      const index = state.tools.findIndex(
        tool => tool.toolId === action.payload.toolId
      );
      if (index !== -1) {
        state.tools[index] = action.payload;
      }
    },
    removeTool: (state, action: PayloadAction<string>) => {
      state.tools = state.tools.filter(tool => tool.toolId !== action.payload);
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setEditingTool: (state, action: PayloadAction<UltravoxToolData | null>) => {
      state.editingTool = action.payload;
    },
    setToolCreatorOpen: (state, action: PayloadAction<boolean>) => {
      state.isToolCreatorOpen = action.payload;
    },
    clearTools: state => {
      state.tools = [];
      state.error = null;
    },

    // Tool creation form actions
    setToolData: (state, action: PayloadAction<Partial<ToolData>>) => {
      state.toolData = { ...state.toolData, ...action.payload };
    },
    resetToolData: state => {
      state.toolData = initialToolData;
      state.errors = {};
      state.hasAttemptedSave = false;
    },
    setErrors: (state, action: PayloadAction<Record<string, string>>) => {
      state.errors = action.payload;
    },
    clearError: (state, action: PayloadAction<string>) => {
      delete state.errors[action.payload];
    },
    setHasAttemptedSave: (state, action: PayloadAction<boolean>) => {
      state.hasAttemptedSave = action.payload;
    },

    // Header management actions
    setIsAddingHeader: (state, action: PayloadAction<boolean>) => {
      state.isAddingHeader = action.payload;
    },
    setIsEditingHeader: (state, action: PayloadAction<boolean>) => {
      state.isEditingHeader = action.payload;
    },
    setEditingHeaderIndex: (state, action: PayloadAction<number>) => {
      state.editingHeaderIndex = action.payload;
    },
    setNewHeader: (state, action: PayloadAction<Partial<ToolHeader>>) => {
      state.newHeader = { ...state.newHeader, ...action.payload };
    },
    resetNewHeader: state => {
      state.newHeader = initialHeader;
    },
    addHeader: (state, action: PayloadAction<ToolHeader>) => {
      state.toolData.headers.push(action.payload);
    },
    updateHeader: (
      state,
      action: PayloadAction<{ index: number; header: ToolHeader }>
    ) => {
      state.toolData.headers[action.payload.index] = action.payload.header;
    },
    removeHeader: (state, action: PayloadAction<number>) => {
      state.toolData.headers.splice(action.payload, 1);
    },

    // Parameter management actions
    setIsAddingParameter: (state, action: PayloadAction<boolean>) => {
      state.isAddingParameter = action.payload;
    },
    setIsEditingParameter: (state, action: PayloadAction<boolean>) => {
      state.isEditingParameter = action.payload;
    },
    setEditingParameterIndex: (state, action: PayloadAction<number>) => {
      state.editingParameterIndex = action.payload;
    },
    setNewParameter: (state, action: PayloadAction<Partial<ToolParameter>>) => {
      state.newParameter = { ...state.newParameter, ...action.payload };
    },
    resetNewParameter: state => {
      state.newParameter = initialParameter;
    },
    addParameter: (state, action: PayloadAction<ToolParameter>) => {
      state.toolData.parameters.push(action.payload);
    },
    updateParameter: (
      state,
      action: PayloadAction<{ index: number; parameter: ToolParameter }>
    ) => {
      state.toolData.parameters[action.payload.index] =
        action.payload.parameter;
    },
    removeParameter: (state, action: PayloadAction<number>) => {
      state.toolData.parameters.splice(action.payload, 1);
    },

    // Initialize form for editing
    initializeFormForEditing: (
      state,
      action: PayloadAction<UltravoxToolData>
    ) => {
      const editingTool = action.payload;

      // Helper function to map Ultravox location enums to internal format
      const mapUltravoxLocationToInternal = (
        location: string
      ): 'query' | 'header' | 'path' | 'body' => {
        const locationMap: Record<
          string,
          'query' | 'header' | 'path' | 'body'
        > = {
          PARAMETER_LOCATION_QUERY: 'query',
          PARAMETER_LOCATION_HEADER: 'header',
          PARAMETER_LOCATION_PATH: 'path',
          PARAMETER_LOCATION_BODY: 'body',
        };
        return locationMap[location] || 'query';
      };

      // Add new function for reverse knownValue mapping
      const mapUltravoxKnownValueToInternal = (knownValue: string): string => {
        const knownValueMap: Record<string, string> = {
          KNOWN_PARAM_CALL_ID: 'Call ID',
          KNOWN_PARAM_CALL_STATE: 'Call State',
          KNOWN_PARAM_CONVERSATION_HISTORY: 'Conversation History',
          KNOWN_PARAM_OUTPUT_SAMPLE_RATE: 'Output Sample Rate',
        };
        return knownValueMap[knownValue] || '';
      };

      // Add reverse mapping function for editing
      const mapDefaultReactionToAction = (
        defaultReaction: string
      ): 'default' | 'speaks' | 'listens' | 'speaks_once' => {
        const reactionMap: Record<
          string,
          'default' | 'speaks' | 'listens' | 'speaks_once'
        > = {
          AGENT_REACTION_UNSPECIFIED: 'default',
          AGENT_REACTION_SPEAKS: 'speaks',
          AGENT_REACTION_LISTENS: 'listens',
          AGENT_REACTION_SPEAKS_ONCE: 'speaks_once',
        };
        return reactionMap[defaultReaction] || 'default';
      };

      state.toolData = {
        name: editingTool.name,
        description: editingTool.definition.description,
        toolType: editingTool.definition.http ? 'HTTP' : 'Client',
        customEndpointUrl: editingTool.definition.http?.baseUrlPattern || '',
        httpMethod: (editingTool.definition.http?.httpMethod as any) || 'GET',
        timeoutSeconds: parseInt(
          editingTool.definition.timeout?.replace('s', '') ?? '20'
        ),
        // Extract headers from staticParameters with header location
        headers:
          editingTool.definition.staticParameters
            ?.filter(param => param.location === 'PARAMETER_LOCATION_HEADER')
            ?.map(param => ({
              key: param.name,
              value: param.value,
              required: false, // Default to false, can be enhanced later
            })) || [],
        parameters: [
          ...(editingTool.definition.dynamicParameters?.map(param => {
            // Check if this is a custom schema by looking for non-standard fields
            const isCustomSchema =
              param.schema &&
              (!param.schema.type ||
                Object.keys(param.schema).some(
                  key => !['type', 'description', 'enum'].includes(key)
                ));

            return {
              name: param.name,
              description: param.schema?.description || '',
              type: isCustomSchema
                ? ('custom' as const)
                : (param.schema?.type as any) || 'string',
              location: mapUltravoxLocationToInternal(param.location),
              parameterType: 'dynamic' as const,
              required: param.required,
              knownValue: '',
              customSchema: isCustomSchema
                ? {
                    _rawValue: JSON.stringify(param.schema, null, 2),
                    _isInvalid: false,
                  }
                : undefined,
            };
          }) || []),
          // Filter out header parameters from staticParameters since they're handled separately in headers array
          ...(editingTool.definition.staticParameters
            ?.filter(param => param.location !== 'PARAMETER_LOCATION_HEADER')
            ?.map(param => ({
              name: param.name,
              description: '',
              type: 'string' as const,
              location: mapUltravoxLocationToInternal(param.location),
              parameterType: 'static' as const,
              required: false,
              knownValue: '',
              value: param.value,
            })) || []),
          ...(editingTool.definition.automaticParameters?.map(param => ({
            name: param.name,
            description: '',
            type: 'string' as const,
            location: mapUltravoxLocationToInternal(param.location),
            parameterType: 'automatic' as const,
            required: false,
            knownValue: mapUltravoxKnownValueToInternal(param.knownValue),
          })) || []),
        ],
        action: mapDefaultReactionToAction(
          editingTool.definition.defaultReaction || 'AGENT_REACTION_UNSPECIFIED'
        ),
        staticResponse:
          editingTool.definition.staticResponse?.responseText || '',
        precomputable: editingTool.definition.precomputable || false,
        staticResponseEnabled:
          !!editingTool.definition.staticResponse?.responseText,
      };
    },
  },
});

export const {
  setTools,
  addTool,
  updateTool,
  removeTool,
  setLoading,
  setError,
  setEditingTool,
  setToolCreatorOpen,
  clearTools,
  setToolData,
  resetToolData,
  setErrors,
  clearError,
  setHasAttemptedSave,
  setIsAddingHeader,
  setIsEditingHeader,
  setEditingHeaderIndex,
  setNewHeader,
  resetNewHeader,
  addHeader,
  updateHeader,
  removeHeader,
  setIsAddingParameter,
  setIsEditingParameter,
  setEditingParameterIndex,
  setNewParameter,
  resetNewParameter,
  addParameter,
  updateParameter,
  removeParameter,
  initializeFormForEditing,
} = toolsSlice.actions;

export default toolsSlice.reducer;
